import { useCallback, useRef, useState } from 'react';

import { resolveUsers, MentionApiError } from '../api';
import { useMentionContext } from '../MentionProvider';
import type { ResolvedUser } from '../types';

// Configuration aligned with Bedrock standards and UX best practices
const MAX_RESULTS = 10; // Industry standard: 5-10 results (Gmail=5, Slack=8-10, GitHub=5-8)
const RATE_LIMIT_COOLDOWN_MS = 30_000;

// NOTE: Frontend caching has been removed. Backend caching at the IdentityService
// layer is far superior because:
// 1. Shared across all users (reduces load on AC User API)
// 2. Memory-efficient (single cache instance vs. per-browser duplication)
// 3. Easy cache invalidation (centralized control)
// 4. Consistent data (single source of truth)
// Backend cache uses: TTL=30s, capacity=100 (aligned with Bedrock standards)

export interface UseMentionTypeaheadReturn {
  items: readonly ResolvedUser[];
  isLoading: boolean;
  error: string | null;
  search: (query: string, entityId: string, sourceRecordUrn?: string) => void;
  clear: () => void;
  isRateLimited: boolean;
}

export function useMentionTypeahead(): UseMentionTypeaheadReturn {
  const { baseUrl, getAccessToken, debounceMs } = useMentionContext();
  const [items, setItems] = useState<readonly ResolvedUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const clear = useCallback(() => {
    clearTimeout(timerRef.current);
    setItems([]);
    setIsLoading(false);
    setError(null);
  }, []);

  const search = useCallback(
    (query: string, entityId: string, sourceRecordUrn?: string) => {
      clearTimeout(timerRef.current);
      if (!query.trim() || isRateLimited) {
        clear();
        return;
      }

      setIsLoading(true);
      setError(null);

      timerRef.current = setTimeout(async () => {
        try {
          const token = await getAccessToken();
          const res = await resolveUsers(baseUrl, token, {
            entityId,
            q: query,
            ...(sourceRecordUrn != null && { sourceRecordUrn }),
            limit: MAX_RESULTS,
          });

          const users = res.users.slice(0, MAX_RESULTS);

          setItems(users);
          setError(null);
        } catch (err) {
          if (err instanceof MentionApiError && err.status === 429) {
            setIsRateLimited(true);
            setError('Too many requests. Please wait.');
            setTimeout(
              () => setIsRateLimited(false),
              err.retryAfter ? err.retryAfter * 1000 : RATE_LIMIT_COOLDOWN_MS,
            );
          } else {
            setError('Unable to search users.');
          }
          setItems([]);
        } finally {
          setIsLoading(false);
        }
      }, debounceMs);
    },
    [baseUrl, getAccessToken, debounceMs, isRateLimited, clear],
  );

  return { items, isLoading, error, search, clear, isRateLimited };
}
